import React, {
    FunctionComponent,
    ReactElement,
    useCallback,
    useEffect,
    useState,
} from "react";

import { Typography, styled, useMediaQuery, useTheme } from "@mui/material";

import * as Yup from "yup";
import {
    DataGrid,
    GridColDef,
    GridRowParams,
    GridToolbar,
    GridValueGetterParams,
} from "@mui/x-data-grid";
import { format } from "date-fns";
import { useFormik } from "formik";
import { useNavigate } from "react-router-dom";

import { DataGridFooter, EmptyRecords } from "../../components/common";
import { useGetBulkOrders } from "../../sdk/hooks";
import { IPage } from "../../types";

const Root = styled("section")`
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing(2)};

    padding: ${({ theme }) => theme.spacing(3)};

    ${({ theme }) => `
    ${theme.breakpoints.down("sm")} {
      padding: ${theme.spacing(2)};
    }
  `}
`;

const Heading = styled(Typography)`
    font-size: ${({ theme }) => theme.spacing(3)};
    font-weight: 500;
`;

const StyledDataGrid = styled(DataGrid)`
    .MuiDataGrid-cell:focus {
        outline: none;
    }
`;

const TitleContainer = styled("div")`
    display: flex;
    justify-content: space-between;
    align-items: center;
`;

const columns: GridColDef[] = [
    {
        field: "name",
        headerName: "Name",
        flex: 0.2,
        minWidth: 160,
        valueGetter: (params: GridValueGetterParams) =>
            `${params.row.firstName} ${params.row.lastName}`,
    },
    {
        field: "phoneNumber",
        headerName: "Phone Number",
        flex: 0.2,
        minWidth: 150,
        valueGetter: (params: GridValueGetterParams) =>
            params.row.phoneNumber ?? "",
    },
    {
        field: "emailAddress",
        headerName: "Email",
        flex: 0.2,
        minWidth: 200,
        valueGetter: (params: GridValueGetterParams) =>
            params.row.emailAddress || "NA",
    },
    {
        field: "createdAt",
        headerName: "Received on",
        flex: 0.15,
        minWidth: 150,
        valueGetter: (params: GridValueGetterParams) =>
            params.row.createdAt
                ? format(new Date(params.row.createdAt), "do MMMM yyyy")
                : "NA",
    },
];

const filterSchema = Yup.object({
    limit: Yup.number(),
    cursor: Yup.string().nullable(),
    direction: Yup.mixed().oneOf(["after", "before"]),
});

export const BulkOrdersScreen: FunctionComponent = (): ReactElement => {
    const navigate = useNavigate();
    const theme = useTheme();
    const smallScreen = useMediaQuery(theme.breakpoints.down("sm"));

    /*
     * Email is the widest column and the least scannable, so it drops out on a
     * phone — it is one tap away on the request itself. Held as state rather
     * than pinned, so the toolbar can still bring it back.
     */
    const [columnVisibilityModel, setColumnVisibilityModel] = useState<
        Record<string, boolean>
    >({});

    useEffect(() => {
        setColumnVisibilityModel({ emailAddress: !smallScreen });
    }, [smallScreen]);

    const [page, setPage] = useState<IPage>({
        limit: 20,
        cursor: null,
        direction: "after",
    });

    const { values, setFieldValue } = useFormik({
        initialValues: {
            limit: page.limit,
            cursor: page.cursor,
            direction: page.direction,
        },
        validationSchema: filterSchema,
        onSubmit: () => {
            return;
        },
    });

    const getBulkOrdersQuery = useGetBulkOrders({
        limit: values.limit,
        cursor: values.cursor ?? undefined,
        direction: values.direction,
        options: {
            queryKey: ["bulkOrders"],
        },
    });

    const handleNavigateBack = useCallback(() => {
        setPage((prevPage) => ({
            ...prevPage,
            cursor: getBulkOrdersQuery.data?.cursors?.previous ?? "",
            direction: "before",
        }));
        setFieldValue(
            "cursor",
            getBulkOrdersQuery.data?.cursors?.previous ?? "",
        );
        setFieldValue("direction", "before");
    }, [getBulkOrdersQuery.data?.cursors?.previous, setFieldValue]);

    const handleNavigateForward = useCallback(() => {
        setPage((prevPage) => ({
            ...prevPage,
            cursor: getBulkOrdersQuery.data?.cursors?.next ?? "",
            direction: "after",
        }));
        setFieldValue("cursor", getBulkOrdersQuery.data?.cursors?.next ?? "");
        setFieldValue("direction", "after");
    }, [getBulkOrdersQuery.data?.cursors?.next, setFieldValue]);

    const handleRowClick = useCallback(
        (params: GridRowParams) => {
            navigate(`/bulk-orders/${params.row.id}`);
        },
        [navigate],
    );

    return (
        <Root>
            <TitleContainer>
                <Heading>Bulk Order Requests</Heading>
            </TitleContainer>

            {getBulkOrdersQuery.isSuccess &&
                getBulkOrdersQuery.data?.records?.length === 0 && (
                    <EmptyRecords>No bulk order requests yet</EmptyRecords>
                )}

            {getBulkOrdersQuery.isSuccess &&
                getBulkOrdersQuery.data?.records?.length > 0 && (
                    <StyledDataGrid
                        autoHeight={true}
                        rows={getBulkOrdersQuery.data?.records ?? []}
                        columns={columns}
                        columnVisibilityModel={columnVisibilityModel}
                        onColumnVisibilityModelChange={setColumnVisibilityModel}
                        onRowClick={handleRowClick}
                        loading={getBulkOrdersQuery.isLoading}
                        slots={{
                            toolbar: GridToolbar,
                            footer: () => (
                                <DataGridFooter
                                    hasNextRecords={Boolean(
                                        getBulkOrdersQuery.data.cursors.next,
                                    )}
                                    hasPreviousRecords={Boolean(
                                        getBulkOrdersQuery.data.cursors
                                            .previous,
                                    )}
                                    onNavigateBack={handleNavigateBack}
                                    onNavigateForward={handleNavigateForward}
                                />
                            ),
                        }}
                        slotProps={{
                            toolbar: {
                                showQuickFilter: true,
                                csvOptions: {
                                    disableToolbarButton: false,
                                },
                                printOptions: {
                                    disableToolbarButton: true,
                                },
                            },
                        }}
                    />
                )}
        </Root>
    );
};
